import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ReactFlow, 
  ReactFlowProvider, 
  Controls, 
  Background, 
  useReactFlow 
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { X, ChevronDown, Monitor, Search, Info, ZoomIn, ZoomOut, Maximize } from "lucide-react";
import { Header1 } from "../comp/themes/modern/home/header/Header1";

import { useSite } from "../../../store/useSite";
import { useFloorPlanner } from "../../../store/useFloorPlanner";
import { useExhibitorStore } from "../../../store/useExhibitorStore";
import { getPublicExhibitorCategories } from "../../../services/exhibitor";
import { getPublicFloorMap } from "../../../services/floorPlan";

// Custom Nodes
import { BoothNode } from "../../floor-planner/nodes/BoothNode";
import { GateNode } from "../../floor-planner/nodes/GateNode";
import { TextNode } from "../../floor-planner/nodes/TextNode";
import { ImageNode } from "../../floor-planner/nodes/ImageNode";
import { BorderNode } from "../../floor-planner/nodes/BorderNode";
import { RectangleNode } from "../../floor-planner/nodes/RectangleNode";
import { PentagonNode } from "../../floor-planner/nodes/PentagonNode";
import { HexagonNode } from "../../floor-planner/nodes/HexagonNode";
import { CircleNode } from "../../floor-planner/nodes/CircleNode";
import { TriangleNode } from "../../floor-planner/nodes/TriangleNode";
import { StarNode } from "../../floor-planner/nodes/StarNode";
import { ArrowNode } from "../../floor-planner/nodes/ArrowNode";
import { LineNode } from "../../floor-planner/nodes/LineNode";

import RightSidePanel from "../../sidebar/RightSideBar";
import { toast } from "react-toastify";
import SizeShow from "../../booths/SizeShow";

const nodeTypes = {
  booth: BoothNode,
  gate: GateNode,
  text: TextNode,
  image: ImageNode,
  border: BorderNode,
  rectangle: RectangleNode,
  pentagon: PentagonNode,
  hexagon: HexagonNode,
  circle: CircleNode,
  triangle: TriangleNode,
  star: StarNode,
  arrow: ArrowNode,
  line: LineNode,
};

const CustomControls = () => {
    const { zoomIn, zoomOut, fitView } = useReactFlow();
    return (
        <div className="absolute bottom-10 right-10 flex flex-col gap-2 z-10">
            <button onClick={() => zoomIn()} className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 text-blue-600 transition-colors border border-gray-100">
                <ZoomIn className="w-5 h-5" />
            </button>
            <button onClick={() => zoomOut()} className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 text-blue-600 transition-colors border border-gray-100">
                <ZoomOut className="w-5 h-5" />
            </button>
            <button onClick={() => fitView({ padding: 0.2, duration: 800 })} className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 text-blue-600 transition-colors border border-gray-100">
                <Maximize className="w-5 h-5" />
            </button>
            <button className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 text-blue-600 transition-colors border border-gray-100">
                <Info className="w-5 h-5" />
            </button>
        </div>
    );
};

const BoothLegend = ({ eventSettings }) => {
    return (
        <div className="absolute bottom-10 left-10 bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-gray-100 z-10 flex flex-col gap-3 min-w-[140px]">
            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2 mb-1">Status Legend</h4>
            <div className="flex items-center gap-3">
                <div 
                    className="w-3.5 h-3.5 rounded-md border border-gray-200 shadow-sm"
                    style={{ backgroundColor: eventSettings?.booth_color_available || "#ffffff" }}
                />
                <span className="text-xs font-bold text-gray-700">Available</span>
            </div>
            <div className="flex items-center gap-3">
                <div 
                    className="w-3.5 h-3.5 rounded-md shadow-sm"
                    style={{ backgroundColor: eventSettings?.booth_color_assigned || "#FF69B4" }}
                />
                <span className="text-xs font-bold text-gray-700">Booked</span>
            </div>
            <div className="flex items-center gap-3">
                <div 
                    className="w-3.5 h-3.5 rounded-md shadow-sm"
                    style={{ backgroundColor: eventSettings?.booth_color_reserved || "#FFB6C1" }}
                />
                <span className="text-xs font-bold text-gray-700">On Hold</span>
            </div>
        </div>
    );
};

const FloorMapContent = ({ floors, selectedFloor, setSelectedBooth, setIsBoothPanelOpen, eventSettings, allBooths }) => {

    const { fitBounds, fitView } = useReactFlow();
    
    const floorData = useMemo(() => floors.find(f => f.id === selectedFloor), [floors, selectedFloor]);
    
    const nodes = useMemo(() => {
        if (!floorData?.react_flow_data?.nodes) return [];
        return floorData.react_flow_data.nodes.map(node => {
            const isBooth = node.type === 'booth' || node.type === 'boothNode';
            if (isBooth) {
                let bgColor = "#FFFFFF";
                const status = node.data?.status || "available";
                if (status === "assigned") bgColor = eventSettings?.booth_color_assigned || "#FF69B4";
                else if (status === "reserved" || status === "onHold") bgColor = eventSettings?.booth_color_reserved || "#FFB6C1";
                else bgColor = eventSettings?.booth_color_available || "#FFFFFF";

                const processedNode = {
                    ...node,
                    type: 'booth',
                    draggable: false,
                    selectable: true,
                    data: { ...node.data, backgroundColor: bgColor, locked: true }
                };
                processedNode.data.company_logo =
                    node.data?.company_logo || node.data?.logo;

                // Hydrate company details from live booths data if missing in node.data
                const boothId = node.data?.boothId;
                if (boothId && allBooths?.length > 0) {
                    const invBooth = allBooths.find(b => String(b.id) === String(boothId));
                    if (invBooth && invBooth.exhibitor) {
                        processedNode.data = {
                            ...processedNode.data,
                            company_name: invBooth.exhibitor.name || node.data.company_name,
                            company_logo: invBooth.exhibitor.logo || node.data.company_logo || node.data.logo,
                            status: invBooth.status || node.data.status || "assigned",
                        };
                    }
                }
                return processedNode;
            }
            return { ...node, draggable: false, selectable: false, data: { ...node.data, locked: true } };
        });
    }, [floorData, eventSettings, allBooths]);

    useEffect(() => {
        if (!floorData) return;
        const timer = setTimeout(() => {
            const focusAreas = floorData.focus_areas || [];
            const activeFocus = focusAreas.find(f => f.status === 'active') || focusAreas[0];
            if (activeFocus) {
                fitBounds({
                    x: activeFocus.focus_x,
                    y: activeFocus.focus_y,
                    width: activeFocus.focus_width,
                    height: activeFocus.focus_height
                }, { padding: 0.1, duration: 800 });
            } else {
                fitView({ padding: 0.2, duration: 800 });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [selectedFloor, fitBounds, fitView, floorData]);

    return (
        <ReactFlow
            nodes={nodes}
            edges={[]}
            nodeTypes={nodeTypes}
            onNodeClick={(e, node) => {
                if (node.type === 'booth') {
                    setSelectedBooth(node);
                    setIsBoothPanelOpen(true);
                }
            }}
            panOnScroll zoomOnScroll panOnDrag
            minZoom={0.2} maxZoom={4}
        >
            <Background color="#f0f2f5" gap={20} />
            <CustomControls />
            <BoothLegend eventSettings={eventSettings} />
        </ReactFlow>
    );
};

export default function SiteExhibitorFloorPlan() {
    const { eventId ="538", slug } = useParams();
    const navigate = useNavigate();
    const { siteData } = useSite();
    const [floors, setFloors] = useState([]);
    const [selectedFloor, setSelectedFloor] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [eventSettings, setEventSettings] = useState(null);
    const [categories, setCategories] = useState([]);
    const [allBooths, setAllBooths] = useState([]);

    
    // Sidebar state
    const isBoothPanelOpen = useExhibitorStore(s => s.isBoothPanelOpen);
    const setIsBoothPanelOpen = useExhibitorStore(s => s.setIsBoothPanelOpen);
    const selectedBooth = useExhibitorStore(s => s.selectedBooth);
    const setSelectedBooth = useExhibitorStore(s => s.setSelectedBooth);
    const setSelectedCategory = useExhibitorStore(s => s.setSelectedCategory);
    const setIsApplying = useExhibitorStore(s => s.setIsApplying);
    const setApplyStep = useExhibitorStore(s => s.setApplyStep);

    useEffect(() => {
        const fetchData = async () => {
            if (!eventId) {
                setIsLoading(false);
                return;
            }
            try {
                // Fetch Floor Plans
                const fRes = await getPublicFloorMap(eventId);
                const fData = fRes.data;
                const root = fData.data || fData;

                if (root?.floorPlans) {
                    const processed = root.floorPlans.map(fp => ({
                        id: fp.id.toString(),
                        name: fp.floor_name,
                        react_flow_data: typeof fp.react_flow_data === 'string' ? JSON.parse(fp.react_flow_data) : fp.react_flow_data,
                        focus_areas: fp.focus_areas || []
                    }));
                    setFloors(processed);
                    if (processed.length) setSelectedFloor(processed[0].id);
                }

                if (root?.booths || root?.all_booths) {
                    setAllBooths(root.booths || root.all_booths);
                }


                if (root?.event_settings) {
                    setEventSettings(root.event_settings);
                    useFloorPlanner.getState().setEventSettings(root.event_settings);
                }

                // Fetch Categories for sidebar info
                const cRes = await getPublicExhibitorCategories(eventId);
                const cData = cRes.data;
                if (cData.message === "success" || cData.success) {
                    const apiCategories = cData.data?.data || cData.data || [];
                    const safeCategories = apiCategories.map((row) => ({
                        id: row.id ?? row.category_id,
                        name: row.category_name ?? row.name ?? "New Category",
                        priceType: row.price_type || "free",
                        price: row.price ?? 0,
                        currency: row.currency || "₹",
                        boothLength: row.booth_height ?? row.booth_length ?? 10,
                        boothBreadth: row.booth_width ?? row.booth_breadth ?? 10,
                        unit: row.booth_dimension_unit || row.unit || "ft",
                        boothMembers: row.max_members ?? row.booth_members ?? row.benefits?.exhibitorPasses?.quantity ?? 2,
                        features: row.features || [],
                    }));
                    setCategories(safeCategories);
                }

            } catch (e) {
                console.error("Error loading floor plan:", e);
                toast.error("Failed to load floor plan data");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [eventId]);

    const handleApply = () => {
        if (!selectedBooth) return;
        const boothData = selectedBooth.data || {};
        const cat = categories.find(c => String(c.id || c.category_id) === String(boothData.category_id));
        
        if (cat) {
            setSelectedCategory({
                ...cat,
                id: cat.id || cat.category_id,
                name: cat.name || cat.category_name
            });
        }
        
        setIsApplying(true);
        setApplyStep(2);
        setIsBoothPanelOpen(false);
        navigate(`/${slug}/exhibitors/category?apply=true&booth=${boothData.number}`);
    };

    const renderBenefit = (isEnabled, label, quantity = null) => (
        <div className="flex items-center gap-3 py-2 text-sm text-gray-700 font-medium">
          <div className="w-5 h-5 flex items-center justify-center">
            {isEnabled ? (
              <div className="bg-green-500 rounded p-0.5"><X size={12} className="text-white rotate-45" strokeWidth={4} /></div>
            ) : (
              <div className="bg-red-500 rounded p-0.5"><X size={12} className="text-white" strokeWidth={4} /></div>
            )}
          </div>
          <span>{quantity && isEnabled ? `${quantity} - ` : ""}{label}</span>
        </div>
    );

    const FloorPlanSkeleton = () => (
  <div className="h-screen flex flex-col bg-[#f0f2f5] overflow-hidden font-sans">
    <Header1 hiddenMenus={true} />
    {/* Floor Plan Header / Toolbar Skeleton */}
    <div className="border-b border-gray-200 bg-white px-6 py-3 flex items-center justify-between z-10">
      <div className="flex items-center gap-3">
        <div className="h-9 w-44 rounded-lg bg-slate-200 animate-pulse" />
        <div className="h-9 w-28 rounded-lg bg-slate-200 animate-pulse hidden sm:block" />
      </div>
      <div className="flex items-center gap-3">
        <div className="h-8 w-24 rounded-full bg-slate-200 animate-pulse" />
        <div className="h-8 w-24 rounded-full bg-slate-200 animate-pulse hidden md:block" />
        <div className="h-8 w-24 rounded-full bg-slate-200 animate-pulse hidden md:block" />
      </div>
    </div>

    {/* Canvas Skeleton */}
    <div className="flex-1 relative bg-[#f8fafc] overflow-hidden flex items-center justify-center p-8">
      {/* Background Grid Pattern Simulation */}
      <div 
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(#cbd5e1 1.5px, transparent 1.5px)",
          backgroundSize: "24px 24px"
        }}
      />

      {/* Simulated Floor layout booths in grid */}
      <div className="relative z-10 w-full max-w-4xl h-full max-h-[560px] border-2 border-dashed border-slate-300 rounded-2xl p-6 flex flex-col justify-between bg-white/50 backdrop-blur-sm">
        {/* Top aisle booths */}
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 sm:h-20 rounded-lg border border-slate-200 bg-slate-100/80 animate-pulse flex flex-col justify-center items-center gap-1.5 p-2">
              <div className="h-3 w-10 rounded bg-slate-200" />
              <div className="h-2 w-14 rounded bg-slate-200" />
            </div>
          ))}
        </div>

        {/* Center aisle / featured booths */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 my-auto py-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 sm:h-32 rounded-xl border border-blue-100 bg-blue-50/60 animate-pulse flex flex-col justify-center items-center gap-2 p-3">
              <div className="h-4 w-16 rounded bg-blue-200/70" />
              <div className="h-3 w-24 rounded bg-blue-200/50" />
              <div className="h-2 w-12 rounded bg-blue-200/40" />
            </div>
          ))}
        </div>

        {/* Bottom aisle booths */}
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 sm:h-20 rounded-lg border border-slate-200 bg-slate-100/80 animate-pulse flex flex-col justify-center items-center gap-1.5 p-2">
              <div className="h-3 w-10 rounded bg-slate-200" />
              <div className="h-2 w-14 rounded bg-slate-200" />
            </div>
          ))}
        </div>
      </div>

      {/* Controls Overlay Skeleton */}
      <div className="absolute bottom-6 left-6 flex flex-col gap-1.5 rounded-lg border border-slate-200 bg-white p-1.5 shadow-md">
        <div className="h-8 w-8 rounded bg-slate-200 animate-pulse" />
        <div className="h-8 w-8 rounded bg-slate-200 animate-pulse" />
        <div className="h-8 w-8 rounded bg-slate-200 animate-pulse" />
      </div>

      {/* Status Pill */}
      <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 border border-slate-200 shadow-sm">
        <div className="h-2.5 w-2.5 rounded-full bg-blue-500 animate-ping" />
        <span className="text-xs font-medium text-slate-600">Loading Floor Plan…</span>
      </div>
    </div>
  </div>
);

    if (isLoading) return <FloorPlanSkeleton />;

    return (
        <div className="h-screen flex flex-col bg-[#f0f2f5] overflow-hidden font-sans">
    
            <Header1 hiddenMenus={true} />
            
            {/* Secondary Header - Floor Selector & Legend */}
          {floors?.length > 1 &&  <div className="absolute top-0 z-50 right-1/2 bg-white border-b border-gray-200 px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="relative group min-w-[160px]">
                        <select 
                            value={selectedFloor} 
                            onChange={(e) => setSelectedFloor(e.target.value)}
                            className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 pr-10 text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
                        >
                            {floors.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>
                </div>
            </div>}

            {/* Main Content Area */}
            <div className="flex-1 relative">
                <ReactFlowProvider>
                    <FloorMapContent 
                        floors={floors} 
                        selectedFloor={selectedFloor} 
                        setSelectedBooth={setSelectedBooth}
                        setIsBoothPanelOpen={setIsBoothPanelOpen}
                        eventSettings={eventSettings}
                        allBooths={allBooths}
                    />
                </ReactFlowProvider>

                {/* Sidebar Overlay */}
                {isBoothPanelOpen && selectedBooth && (() => {
                    const boothData = selectedBooth?.data || {};
                    const cat = categories?.find(c => String(c.id || c.category_id) === String(boothData.category_id))
                             || categories?.find(c => c.name === (boothData.category_name || boothData.exhibitorCategory));
                             
                    const length = boothData.dimensions?.length || selectedBooth?.height || cat?.boothLength;
                    const breadth = boothData.dimensions?.breadth || selectedBooth?.width || cat?.boothBreadth;
                    const unit = eventSettings?.booth_dimension_unit || cat?.unit || boothData.dimensions?.unit || 'feet';

                    const boothPasses = cat?.boothMembers ?? boothData.benefits?.exhibitorPasses?.quantity ?? 2;
                    const features = cat?.features;

                    return (
                        <RightSidePanel
                            isOpen={isBoothPanelOpen && selectedBooth}
                            onClose={() => setIsBoothPanelOpen(false)}
                            title="Booth Details"
                        >
                            <div className="space-y-8">
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Booth Number</label>
                                    <span className="text-xl font-bold text-gray-900">{boothData.number || "N/A"}</span>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Dimensions</label>
                                    <span className="text-sm font-semibold text-gray-700 inline-flex">
                                        <SizeShow height={length} width={breadth} booth_dimension_unit={unit} />
                                    </span>
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Price</label>
                                    <span className="text-lg font-bold text-gray-900">{eventSettings?.currency || cat?.currency || "₹"} {boothData.price || cat?.price || "0"}</span>
                                </div>
                                
                                <div>
                                    <h4 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-4">Benefits</h4>
                                    <div className="space-y-1">
                                        {(!features || features.length === 0) && boothPasses === null ? (
                                            <p className="text-xs text-gray-400 italic">No benefits info</p>
                                        ) : (
                                            <>
                                                {boothPasses !== null && boothPasses !== undefined && renderBenefit(
                                                    true,
                                                    "Booth Members",
                                                    boothPasses,
                                                )}

                                                {features
                                                    ?.filter((f) => f.visible !== 0)
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((feature) => (
                                                        <div key={feature.id || feature.key}>
                                                            {renderBenefit(
                                                                feature.enabled === 1 || feature.value === "yes",
                                                                feature.name,
                                                                feature.supported_values?.includes("count") && feature.value !== "no" ? feature.value : null
                                                            )}
                                                        </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="pt-8 border-t border-gray-100">
                                    <button 
                                        disabled={boothData.status === "assigned" || boothData.status === "reserved" || boothData.status === "onHold"}
                                        onClick={handleApply}
                                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-500/20"
                                    >
                                        {boothData.status === "assigned" ? "Already Booked" : (boothData.status === "reserved" || boothData.status === "onHold") ? "Reserved" : "Apply to Exhibit"}
                                    </button>
                                </div>
                            </div>
                        </RightSidePanel>
                    );
                })()}

                {/* Footer Logo overlay */}
                <div className="absolute bottom-6 left-8 flex items-center gap-2 pointer-events-none opacity-60">
                     <span className="text-[10px] font-bold text-gray-400 tracking-wider">POWERED BY</span>
                     <img src="/logo-dark.png" alt="Logo" className="h-4" />
                </div>
            </div>
        </div>
    );
}
